/**
 * AbductionEffects.ts
 *
 * Handles visual and audio effects when the player abducts/eats an entity.
 *
 * Effects (from C3):
 * 1. Player "Powerup" animation (8 frames)
 * 2. Floating text showing "$X" biomoney value (fades out)
 * 3. Particle burst of golden coins (one-shot, spray upward)
 * 4. Sound effect "biomoney"
 *
 * C3 Particle config:
 * - rate: 10 particles
 * - spray-cone: 180°
 * - speed: 100, acceleration: -150
 * - grow-rate: 5, timeout: 1s
 * - angle: 270° (upward)
 * - destroy-mode: fade-to-invisible
 */

import Phaser from 'phaser';
import type { Player } from '../entities';
import { audioManager } from '../core/CoreManagerAudio';
import { ParticleRegistry } from './ParticleRegistry';
import { LayerDepths } from '../constants/enums';
import type { AbductionEvent } from '../entities/behaviors/AbductableBehavior';
import { FloatingNotification } from '../ui/FloatingNotification';

export interface AbductionEffectsConfig {
  scene: Phaser.Scene;
  player: Player;
}

export class AbductionEffects {
  private scene: Phaser.Scene;
  private player: Player;
  private particles?: Phaser.GameObjects.Particles.ParticleEmitter;
  private particleManager?: Phaser.GameObjects.Particles.ParticleEmitterManager;

  constructor(config: AbductionEffectsConfig) {
    this.scene = config.scene;
    this.player = config.player;

    // Setup particle emitter
    this.setupParticles();

    // Listen for abduction complete events
    this.scene.events.on('entityAbducted', this.onEntityAbducted, this);

    // Cleanup
    this.scene.events.once('shutdown', this.destroy, this);
    this.scene.events.once('destroy', this.destroy, this);
  }

  /**
   * Setup particle emitter for biomoney effect
   * Golden coin particles that spray upward when entity is eaten
   */
  private setupParticles(): void {
    // Use adornobiomoney texture for golden coin particles
    // Note: C3 size of 8 meant 8px, not 8%, so we use a proper scale
    this.particles = this.scene.add.particles(0, 0, 'adornobiomoney', {
      speed: { min: 80, max: 150 },
      angle: { min: 230, max: 310 }, // Upward spray (270° ± 40°)
      scale: { start: 0.8, end: 1.0 },
      alpha: { start: 1, end: 0 },
      lifespan: 1000,
      gravityY: 50, // Slight downward drift after burst
      quantity: 10,
      emitting: false,
      rotate: { min: 0, max: 360 },
      emitCallback: () => {
        ParticleRegistry.add('coinParticles');
      },
      deathCallback: () => {
        ParticleRegistry.remove('coinParticles');
      },
    });

    this.particles.setDepth(LayerDepths.AbductionParticles);
  }

  /**
   * Handle entity abducted event
   */
  private onEntityAbducted(data: AbductionEvent): void {
    const { bioMatter, notificationText, notificationType } = data;

    // 1. Player Powerup animation
    this.playPlayerPowerupAnimation();

    // 2. Particle burst
    this.emitParticles(this.player.x, this.player.y);

    // 3. Show appropriate notification using FloatingNotification widget
    if (notificationType === 'powerup' && notificationText) {
      FloatingNotification.powerup(this.scene, notificationText, {
        position: { x: this.player.x, y: this.player.y - 40 },
        autoDuration: true,
      });
    } else if (bioMatter > 0) {
      FloatingNotification.biomatter(this.scene, `$${bioMatter}`, {
        position: { x: this.player.x, y: this.player.y - 40 },
        autoDuration: true,
      });
    }

    // 4. Sound effect
    this.playSound();
  }

  /**
   * Play all abduction effects
   */
  playEffects(x: number, y: number, bioMatter: number): void {
    // 1. Player Powerup animation
    this.playPlayerPowerupAnimation();

    // 2. Particle burst
    this.emitParticles(x, y);

    // 3. Floating text using FloatingNotification widget
    if (bioMatter > 0) {
      FloatingNotification.biomatter(this.scene, `$${bioMatter}`, {
        position: { x, y: y - 40 },
        autoDuration: true,
      });
    }

    // 4. Sound effect
    this.playSound();
  }

  /**
   * Play player powerup animation
   */
  private playPlayerPowerupAnimation(): void {
    // Use Player's method which handles blocking eating/default animations
    if (typeof this.player.playPowerupAnimation === 'function') {
      this.player.playPowerupAnimation();
    }
  }

  /**
   * Emit particle burst at position
   */
  private emitParticles(x: number, y: number): void {
    if (this.particles) {
      this.particles.emitParticleAt(x, y - 20, 10); // Emit 10 particles slightly above
    }
  }

  /**
   * Play biomoney sound
   */
  private playSound(): void {
    audioManager.playSfx('sfx_biomoney', 'entity', this.scene);
  }

  /**
   * Cleanup
   */
  destroy(): void {
    // Prevent double destroy
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    this.scene.events.off('entityAbducted', this.onEntityAbducted, this);

    if (this.particles && this.particles.scene) {
      this.particles.destroy();
      this.particles = undefined;
    }
  }

  private isDestroyed = false;
}
